import {
  CompanyBasicInfo,
  CompanyData,
  CompanyDetail,
  CompanyListItemBasicData,
  CompanyOrt,
  CompanyUpdateData,
  CreateCompanyOrt,
} from '@types';
import {
  CreateCompanyGrossHourlyRatePayload,
  GetCompanyGrossHourlyRateResponse,
  GetCompanyPartnerRelationsResponse,
} from './company-service.types';
import { HqApiService } from './hqApiService';

export class CompanyService {
  constructor(private readonly hqApiService: HqApiService) {}

  /**
   * Create a new company
   */
  public createCompany = async (company: CompanyData) => {
    await this.hqApiService.create({
      path: `companies`,
      body: company,
    });
  };

  /**
   * Update an existing company
   */
  public updateCompany = async (companyId: string, payload: CompanyUpdateData) => {
    if (!companyId) {
      throw new Error("Can't update company, missing company ID");
    }

    await this.hqApiService.update({
      path: `companies/${companyId}`,
      body: payload,
    });
  };

  /**
   * Get a single company
   */
  public getCompanyDetail = async (companyId: string): Promise<CompanyDetail> => {
    const company = await this.hqApiService.get<CompanyDetail>({
      path: `companies/${companyId}`,
    });

    return company;
  };

  public getCompanyBasicInfoByWorkId = async (workId: string): Promise<CompanyBasicInfo> => {
    const company = await this.hqApiService.get<CompanyDetail>({
      path: `companies/basic-info-by-work-id/${workId}`,
    });
    return company;
  };

  /**
   * Get the companies where the FW has claimed at least a shift
   */
  public getCompaniesForFlexWorker = async (
    flexWorkerId: string
  ): Promise<CompanyListItemBasicData[]> => {
    const response = await this.hqApiService.get<CompanyListItemBasicData[]>({
      path: `companies/for-flexworker/${flexWorkerId}`,
    });

    return response;
  };

  /**
   * Archive the company
   */
  public archiveCompany = async (companyId: string) =>
    await this.hqApiService.delete<undefined>({
      path: `companies/${companyId}`,
    });

  /**
   * Get a list of default ORTs
   */
  public getOrts = async (companyId: string) => {
    const orts = await this.hqApiService.get<CompanyOrt[]>({
      path: `companies/${companyId}/orts`,
    });

    return orts;
  };

  public createOrt = async (companyId: string, ort: CreateCompanyOrt) => {
    await this.hqApiService.create({
      path: `companies/${companyId}/ort`,
      body: ort,
    });
  };

  public getPartnerRelations = async (
    companyId: string
  ): Promise<GetCompanyPartnerRelationsResponse[]> => {
    return await this.hqApiService.get<GetCompanyPartnerRelationsResponse[]>({
      path: `companies/${companyId}/partner-relations`,
    });
  };

  public getCompanyGrossHourlyRates = async (companyId: string) => {
    const companyEmploymentTypes = await this.hqApiService.get<GetCompanyGrossHourlyRateResponse>({
      path: `companies/${companyId}/gross-hourly-rates/`,
    });

    return companyEmploymentTypes;
  };

  public createCompanyGrossHourlyRate = async ({
    companyId,
    ...data
  }: CreateCompanyGrossHourlyRatePayload): Promise<void> => {
    await this.hqApiService.create({
      path: `companies/${companyId}/gross-hourly-rate`,
      body: data,
    });
  };
}
